import { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  const handleScroll = useCallback(() => {
    setIsScrolled(window.scrollY > 60);
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  const isHome = location.pathname === '/';

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled || !isHome
            ? 'bg-background-50/95 backdrop-blur-md border-b border-foreground-100/60'
            : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 md:px-6 flex items-center justify-between h-16">
          <Link
            to="/"
            className={`text-lg font-heading font-bold whitespace-nowrap transition-colors duration-300 ${
              isScrolled || !isHome ? 'text-foreground-950' : 'text-background-50'
            }`}
          >
            Superbuyspread
          </Link>

          <div className="hidden md:flex items-center gap-6">
            <Link
              to="/"
              className={`text-sm font-medium transition-colors duration-200 whitespace-nowrap ${
                isScrolled || !isHome ? 'text-foreground-700 hover:text-foreground-950' : 'text-background-50/80 hover:text-background-50'
              }`}
            >
              Home
            </Link>
            <Link
              to="/superbuy-spreadsheet-guide"
              className={`text-sm font-medium transition-colors duration-200 whitespace-nowrap ${
                isScrolled || !isHome ? 'text-foreground-700 hover:text-foreground-950' : 'text-background-50/80 hover:text-background-50'
              }`}
            >
              Complete Guide
            </Link>
            <Link
              to="/best-superbuy-spreadsheet"
              className={`text-sm font-medium transition-colors duration-200 whitespace-nowrap ${
                isScrolled || !isHome ? 'text-foreground-700 hover:text-foreground-950' : 'text-background-50/80 hover:text-background-50'
              }`}
            >
              Best Finds
            </Link>
            <Link
              to="/superbuy-spreadsheet-shoes"
              className={`text-sm font-medium transition-colors duration-200 whitespace-nowrap ${
                isScrolled || !isHome ? 'text-foreground-700 hover:text-foreground-950' : 'text-background-50/80 hover:text-background-50'
              }`}
            >
              Shoes Guide
            </Link>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className={`px-4 py-2 rounded-full text-sm font-semibold transition-all duration-200 cursor-pointer whitespace-nowrap ${
                isScrolled || !isHome
                  ? 'bg-foreground-900 text-background-50 hover:bg-foreground-800'
                  : 'bg-background-50/15 backdrop-blur-sm text-background-50 border border-background-50/30 hover:bg-background-50/25'
              }`}
            >
              Buy It Now
            </a>
          </div>

          <button
            onClick={() => setIsOpen(!isOpen)}
            className={`md:hidden relative w-10 h-10 flex items-center justify-center rounded-full transition-colors duration-200 cursor-pointer ${
              isScrolled || !isHome
                ? 'text-foreground-950 hover:bg-foreground-100'
                : 'text-background-50 hover:bg-background-50/10'
            }`}
            aria-label="Toggle menu"
          >
            {isOpen ? (
              <i className="ri-close-line text-2xl w-6 h-6 flex items-center justify-center"></i>
            ) : (
              <i className="ri-menu-line text-2xl w-6 h-6 flex items-center justify-center"></i>
            )}
          </button>
        </div>
      </nav>

      {isOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div className="absolute inset-0 bg-foreground-950/60 backdrop-blur-sm" onClick={() => setIsOpen(false)}></div>
          <div className="absolute top-16 left-0 right-0 bottom-0 bg-background-50 overflow-y-auto animate-in slide-in-from-top-2 duration-200">
            <div className="px-5 py-6">
              <p className="text-xs font-semibold tracking-[0.15em] text-foreground-400 font-label uppercase mb-4">Navigation</p>
              <div className="space-y-1 mb-8">
                <Link
                  to="/"
                  className="flex items-center gap-3 px-3 py-3 rounded-xl text-foreground-950 font-medium hover:bg-background-100 transition-colors"
                >
                  <i className="ri-home-4-line text-lg w-6 h-6 flex items-center justify-center text-accent-500"></i>
                  Home
                </Link>
                <Link
                  to="/superbuy-spreadsheet-guide"
                  className="flex items-center gap-3 px-3 py-3 rounded-xl text-foreground-950 font-medium hover:bg-background-100 transition-colors"
                >
                  <i className="ri-book-open-line text-lg w-6 h-6 flex items-center justify-center text-accent-500"></i>
                  Complete Guide
                </Link>
                <Link
                  to="/best-superbuy-spreadsheet"
                  className="flex items-center gap-3 px-3 py-3 rounded-xl text-foreground-950 font-medium hover:bg-background-100 transition-colors"
                >
                  <i className="ri-star-line text-lg w-6 h-6 flex items-center justify-center text-accent-500"></i>
                  Best Finds
                </Link>
                <Link
                  to="/superbuy-spreadsheet-shoes"
                  className="flex items-center gap-3 px-3 py-3 rounded-xl text-foreground-950 font-medium hover:bg-background-100 transition-colors"
                >
                  <i className="ri-footprint-line text-lg w-6 h-6 flex items-center justify-center text-accent-500"></i>
                  Shoes Guide
                </Link>
              </div>

              <p className="text-xs font-semibold tracking-[0.15em] text-foreground-400 font-label uppercase mb-4">Product Categories</p>
              <div className="grid grid-cols-2 gap-1.5 mb-8">
                {categories.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center gap-2.5 px-3 py-3 rounded-xl text-sm font-medium text-foreground-800 hover:bg-background-100 transition-colors cursor-pointer"
                  >
                    <span className="w-8 h-8 rounded-lg bg-accent-100 flex items-center justify-center flex-shrink-0">
                      <i className={`${cat.icon} text-accent-700 text-sm w-5 h-5 flex items-center justify-center`}></i>
                    </span>
                    {cat.name}
                  </a>
                ))}
              </div>

              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="flex items-center justify-center gap-2 w-full px-4 py-3 bg-foreground-900 text-background-50 rounded-full text-sm font-semibold hover:bg-foreground-800 transition-colors cursor-pointer whitespace-nowrap"
              >
                Visit Main Platform
                <i className="ri-arrow-right-up-line text-lg w-5 h-5 flex items-center justify-center"></i>
              </a>
            </div>
          </div>
        </div>
      )}
    </>
  );
}